import { useMemo } from 'react';

import {
  useGroupOrderDeliveryLocationQuery,
  useGroupOrderRestaurantQuery,
} from '../../graphql/GroupOrderLocation.generated';
import { GroupOrderChannel } from '../../GroupOrderSetupScreen.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useGroupOrderLocationDetails = (params: Params) => {
  const { deliveryAddressId, restaurantId } = params;

  const hasDeliveryAddress = deliveryAddressId !== undefined;

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [restaurantResponse] = useGroupOrderRestaurantQuery({
    variables: { id: restaurantId ?? '' },
    pause: !restaurantId || hasDeliveryAddress,
    requestPolicy: 'cache-first',
  });

  const [deliveryLocationResponse] = useGroupOrderDeliveryLocationQuery({
    variables: {
      addressId: deliveryAddressId ?? '',
      input: { addressId: deliveryAddressId ?? '' },
    },
    pause: !hasDeliveryAddress,
    requestPolicy: 'cache-first',
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const isFetching = restaurantResponse.fetching || deliveryLocationResponse.fetching;

  const restaurant = restaurantResponse.data?.restaurant;
  const deliveryLocationData = deliveryLocationResponse.data;
  const deliveryAddress = deliveryLocationData?.address;

  const hasDeliveryDetails = deliveryLocationData?.locationByAddressId?.__typename === 'Location';
  const hasDeliveryInfo = hasDeliveryAddress && hasDeliveryDetails && deliveryAddress;
  const hasRestaurantInfo = restaurantId && restaurant;

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    if (isFetching) {
      return { isFetching: true };
    }

    // ─── Delivery ────────────────────────────────────────────────────────

    if (hasDeliveryInfo) {
      const orderChannel: GroupOrderChannel = 'delivery';

      const deliveryLocation = deliveryLocationData?.locationByAddressId;
      const deliveryRestaurant = deliveryLocation.restaurant;

      return {
        isFetching: false,
        orderChannel,
        location: {
          label: deliveryAddress.name,
          restaurant: deliveryRestaurant,
          address: formatAddress(deliveryAddress),
          secondaryAddress: formatSecondaryAddress(deliveryAddress),
        },
        deliveryOrderDetail: {
          addressId: deliveryAddressId,
          deliveryFee: deliveryLocation.deliveryFee,
          tip: 0,
          vendor: deliveryLocation.vendor.name,
          vendorRestaurantId: deliveryLocation.vendor.restaurantId,
        },
      };
    }

    // ─── Pickup or Outpost ───────────────────────────────────────────────

    if (hasRestaurantInfo) {
      const orderChannel: GroupOrderChannel = restaurant.isOutpost ? 'outpost' : 'pickup';

      return {
        isFetching: false,
        orderChannel,
        location: {
          label: restaurant.name,
          restaurant,
          address: restaurant.address,
          secondaryAddress: formatSecondaryAddress(restaurant),
        },
      };
    }
  }, [
    deliveryAddress,
    deliveryLocationData?.locationByAddressId,
    deliveryAddressId,
    isFetching,
    restaurant,
    hasRestaurantInfo,
    hasDeliveryInfo,
  ]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatAddress = (address: MaybeAddress) =>
  [address?.street, address?.secondaryStreet].filter(Boolean).join(', ');

const formatSecondaryAddress = (address: MaybeSecondaryAddress) =>
  [address?.city, [address?.state, address?.zipCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  restaurantId?: string;
  deliveryAddressId?: string;
};

type MaybeAddress = { street?: string | null; secondaryStreet?: string | null } | undefined | null;
type MaybeSecondaryAddress =
  | { city?: string | null; state?: string | null; zipCode?: string | null }
  | undefined
  | null;
